export type GroupRole = "admin" | "basic_user" | "engineer" | "operator";
export type GlobalRole = "system_user";
export type AccessDeviceTier = "phone" | "tablet" | "desktop";

export type SurfaceKey =
  | "home"
  | "devices"
  | "events"
  | "venues"
  | "reports"
  | "people"
  | "groups"
  | "agents"
  | "agentLog"
  | "engineering"
  | "diagnostics";

export interface GroupMembershipAccess {
  group_id: number;
  role: GroupRole;
  status: string;
}

export interface AccessProfile {
  isLoading: boolean;
  error: string | null;
  userSub: string | null;
  deviceTier: AccessDeviceTier;
  memberships: GroupMembershipAccess[];
  activeRoles: Set<GroupRole>;
  isSystemUser: boolean;
  canAccessSurface: (surface: SurfaceKey) => boolean;
  getSurfaceAccess: (surface: SurfaceKey) => SurfaceAccessDecision;
  hasRoleInGroup: (groupId: number | string | null | undefined, role: GroupRole) => boolean;
  canEngineerOwnerGroup: (groupId: number | string | null | undefined) => boolean;
  canOperateOwnerGroup: (groupId: number | string | null | undefined) => boolean;
  canViewDeviceOperationalStatus: (groupId: number | string | null | undefined) => boolean;
  canViewAnyDeviceOperationalStatus: () => boolean;
}

export interface SurfaceAccessDecision {
  allowed: boolean;
  reason?: "role" | "device";
  message?: string;
}

const alwaysVisibleSurfaces = new Set<SurfaceKey>([
  "home",
  "devices",
  "events",
  "venues",
  "reports",
]);

export const getSurfaceAccessForRoles = (
  surface: SurfaceKey,
  activeRoles: Set<GroupRole>,
  isSystemUser: boolean,
  deviceTier: AccessDeviceTier = "desktop"
): SurfaceAccessDecision => {
  if (isSystemUser) return { allowed: true };
  if (alwaysVisibleSurfaces.has(surface)) return { allowed: true };

  if (deviceTier === "phone" && surface === "engineering") {
    return {
      allowed: false,
      reason: "device",
      message: "Engineering tools are available on tablet and desktop screens.",
    };
  }

  switch (surface) {
    case "people":
    case "groups":
      return activeRoles.has("admin")
        ? { allowed: true }
        : {
            allowed: false,
            reason: "role",
            message: "This surface is available to Admins and System Users.",
          };
    case "engineering":
      return activeRoles.has("engineer")
        ? { allowed: true }
        : {
            allowed: false,
            reason: "role",
            message: "Engineering tools are available to Engineers and System Users.",
          };
    case "agents":
    case "agentLog":
    case "diagnostics":
      return {
        allowed: false,
        reason: "role",
        message: "This surface is available to System Users.",
      };
    default:
      return {
        allowed: false,
        reason: "role",
        message: "This surface is not available for your current role.",
      };
  }
};

export const canAccessSurfaceForRoles = (
  surface: SurfaceKey,
  activeRoles: Set<GroupRole>,
  isSystemUser: boolean,
  deviceTier: AccessDeviceTier = "desktop"
) => getSurfaceAccessForRoles(surface, activeRoles, isSystemUser, deviceTier).allowed;

export const canModifyReferenceDataForRoles = (
  activeRoles: Set<GroupRole>,
  isSystemUser: boolean
) => isSystemUser || activeRoles.has("admin") || activeRoles.has("engineer");

export const hasRoleInGroupForMemberships = (
  memberships: GroupMembershipAccess[],
  groupId: number | string | null | undefined,
  role: GroupRole
) => {
  if (groupId === null || groupId === undefined || groupId === "") return false;
  const normalizedGroupId = Number(groupId);
  if (Number.isNaN(normalizedGroupId)) return false;

  return memberships.some(
    (membership) =>
      membership.status === "active" &&
      membership.group_id === normalizedGroupId &&
      membership.role === role
  );
};

export const canEngineerOwnerGroupForMemberships = (
  memberships: GroupMembershipAccess[],
  isSystemUser: boolean,
  groupId: number | string | null | undefined
) => isSystemUser || hasRoleInGroupForMemberships(memberships, groupId, "engineer");

export const canOperateOwnerGroupForMemberships = (
  memberships: GroupMembershipAccess[],
  isSystemUser: boolean,
  groupId: number | string | null | undefined
) => isSystemUser || hasRoleInGroupForMemberships(memberships, groupId, "operator");

export const canViewDeviceOperationalStatusForMemberships = (
  memberships: GroupMembershipAccess[],
  isSystemUser: boolean,
  groupId: number | string | null | undefined
) =>
  isSystemUser ||
  hasRoleInGroupForMemberships(memberships, groupId, "engineer") ||
  hasRoleInGroupForMemberships(memberships, groupId, "operator");

export const canViewAnyDeviceOperationalStatusForMemberships = (
  memberships: GroupMembershipAccess[],
  isSystemUser: boolean
) =>
  isSystemUser ||
  memberships.some(
    (membership) =>
      membership.status === "active" &&
      (membership.role === "engineer" || membership.role === "operator")
  );
